import React from "react";

import { ChevronDown } from "lucide-react";

import { ACCENT, Modal } from "./Chrome";
import type { XFactorDigestData } from "../../../Types/XFactorBriefing";
import { SECTION_TITLE, buildExplainPanes } from "../lib/derive";

/**
 * Explainability: how the scores were computed, how reliable the data is, and
 * what evidence backs the narrative — answered per section, from the payload's
 * own rationale and metadata. No model call.
 */
const ExplainabilityModal: React.FC<{
  open: boolean;
  onClose: () => void;
  accountName: string;
  digestDate?: string | null;
  data?: XFactorDigestData | null;
}> = ({ open, onClose, accountName, digestDate, data }) => {
  const panes = React.useMemo(() => buildExplainPanes(data), [data]);
  const [active, setActive] = React.useState("summary");
  const [openQ, setOpenQ] = React.useState<string | null>(null);

  const pane = panes.find(p => p.sectionId === active) || panes[0];

  return (
    <Modal
      open={open}
      onClose={onClose}
      width="max-w-3xl"
      title={`${accountName} — ${SECTION_TITLE[active] || pane?.label || ""} Explanation`}
      subtitle={digestDate}
    >
      {/* Underlined tabs, as xfactor renders them. */}
      <div className="mb-4 flex flex-wrap gap-x-4 border-b border-gray-200">
        {panes.map(p => {
          const isActive = p.sectionId === active;
          return (
            <button
              key={p.sectionId}
              type="button"
              onClick={() => {
                setActive(p.sectionId);
                setOpenQ(null);
              }}
              className="-mb-px border-b-2 px-0.5 pb-2 text-sm transition-colors"
              style={{
                borderColor: isActive ? ACCENT : "transparent",
                color: isActive ? ACCENT : "#475569",
                fontWeight: isActive ? 600 : 400,
              }}
            >
              {p.label}
            </button>
          );
        })}
      </div>

      <div className="space-y-2">
        {pane?.questions.map(q => {
          const isOpen = openQ === q.id;
          const hasAnswer = Boolean(q.answer) || q.bullets.length > 0;
          return (
            <div
              key={q.id}
              className="rounded-lg border border-gray-200 bg-white"
            >
              <button
                type="button"
                onClick={() => setOpenQ(isOpen ? null : q.id)}
                className="flex w-full items-start gap-3 px-4 py-3 text-left"
              >
                <span
                  className="mt-0.5 text-[11px] font-semibold"
                  style={{ color: ACCENT }}
                >
                  {q.id}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium text-gray-900">
                    {q.question}
                  </span>
                  <span className="mt-0.5 block text-xs leading-snug text-gray-500">
                    {q.hint}
                  </span>
                </span>
                <ChevronDown
                  size={16}
                  className={`mt-0.5 shrink-0 text-gray-400 transition-transform ${
                    isOpen ? "rotate-180" : ""
                  }`}
                  aria-hidden
                />
              </button>

              {isOpen ? (
                <div className="border-t border-gray-100 px-4 py-3">
                  {hasAnswer ? (
                    <>
                      {q.answer ? (
                        <p className="text-sm leading-relaxed text-gray-700">
                          {q.answer}
                        </p>
                      ) : null}
                      {q.bullets.length ? (
                        <ul className="mt-2 space-y-1">
                          {q.bullets.map((b, i) => (
                            <li
                              key={i}
                              className="text-xs leading-snug text-gray-600 before:mr-1.5 before:text-gray-300 before:content-['•']"
                            >
                              {b}
                            </li>
                          ))}
                        </ul>
                      ) : null}
                    </>
                  ) : (
                    <p className="text-xs italic text-gray-400">
                      This briefing carries no explainability data for that
                      question.
                    </p>
                  )}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </Modal>
  );
};

export default ExplainabilityModal;
